import { useCallback, useContext, useMemo } from 'react';
import DataRoomUploadContext from 'router/subrouters/DataRoom/providers/DataRoomUpload/DataRoomUpload.context';
import DataRoomDocumentsContext from '../../../providers/DataRoomDocuments/DataRoomDocuments.context';
import { getIsNodeFile, getIsNodeFolder } from 'utils';
import { MAX_PERCENTAGE } from 'data/global';

export default function useUploadingPlaceholders(documentId: string) {
  const { currentlyUploading } = useContext(DataRoomUploadContext);
  const { currentlyDisplayedNodes } = useContext(DataRoomDocumentsContext);

  const stillUploading = useMemo(
    () =>
      currentlyUploading.filter(
        ({ isFailed, parentId }) => !isFailed && parentId === documentId,
      ),
    [currentlyUploading, documentId],
  );

  const stillUploadingByType = useMemo(() => {
    const folders: Record<string, typeof stillUploading> = {};
    const files: typeof stillUploading = [];

    stillUploading.forEach((item) => {
      if (item.folderName) {
        if (!folders[item.folderName]) {
          folders[item.folderName] = [];
        }
        folders[item.folderName].push(item);
      } else {
        files.push(item);
      }
    });

    return { folders, files };
  }, [stillUploading]);

  const placeholdersForRender = useMemo(() => {
    const renderedFolderNodes = currentlyDisplayedNodes.filter(getIsNodeFolder);
    const renderedFileNodes = currentlyDisplayedNodes.filter(getIsNodeFile);

    return {
      folders: Object.keys(stillUploadingByType.folders).filter((folderName) =>
        renderedFolderNodes.every(({ name }) => name !== folderName),
      ),
      files: stillUploadingByType.files.filter(
        ({ name: fileName, replacingFileName }) =>
          renderedFileNodes.every(
            ({ name }) => ![fileName, replacingFileName].includes(name),
          ),
      ),
    };
  }, [
    currentlyDisplayedNodes,
    stillUploadingByType.files,
    stillUploadingByType.folders,
  ]);

  const getIsStillUploadingNode = useCallback(
    (node) => {
      const isFolder = getIsNodeFolder(node);

      if (isFolder)
        return Object.entries(stillUploadingByType.folders).some(
          ([folderName, files]) =>
            node.name === folderName &&
            files.some(({ percentage }) => percentage !== MAX_PERCENTAGE),
        );

      return stillUploadingByType.files.some(
        ({ name: fileName, replacingFileName, percentage }) =>
          [fileName, replacingFileName].includes(node.name) &&
          percentage !== MAX_PERCENTAGE,
      );
    },
    [stillUploadingByType],
  );

  const displayedNodeIdsUploading = useMemo(
    () =>
      currentlyDisplayedNodes
        .filter(getIsStillUploadingNode)
        .map(({ _id }) => _id),
    [currentlyDisplayedNodes, getIsStillUploadingNode],
  );

  return { placeholders: placeholdersForRender, displayedNodeIdsUploading };
}
